import type { HTMLAttributes } from 'react';
import styled, { css, type RuleSet } from 'styled-components';
import Image from 'components/generic/Image';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import DiagonalLines from './DiagonalLines';
import { baseOutline, DisplayTypeSize, selectedStyling, swatchStateStyling } from './shared/styles';
import type { ColorStyleProps, ColorSwatchProps } from './shared/types';

function getColorStyling($colors: ColorStyleProps['$colors']): RuleSet {
    switch ($colors.length) {
        case 0:
            return css``;
        case 1:
            return css`
                background-color: ${$colors[0]};
            `;
        case 2:
            return css`
                background-image: linear-gradient(
                    90deg,
                    ${$colors[0]} 0%,
                    ${$colors[0]} 50%,
                    ${$colors[1]} 50%,
                    ${$colors[1]} 100%
                );
            `;
        case 3:
            return css`
                background-image: linear-gradient(
                    90deg,
                    ${$colors[0]} 0%,
                    ${$colors[0]} 33%,
                    ${$colors[1]} 33%,
                    ${$colors[1]} 66%,
                    ${$colors[2]} 66%,
                    ${$colors[2]} 100%
                );
            `;
        case 4:
        default:
            return css`
                background-image: conic-gradient(
                    from 270deg at 51% 49%,
                    ${$colors[0]} 25%,
                    ${$colors[1]} 25%,
                    ${$colors[1]} 50%,
                    ${$colors[2]} 50%,
                    ${$colors[2]} 75%,
                    ${$colors[3]} 75%,
                    ${$colors[3]} 100%
                );
            `;
    }
}

function getDimensions($size) {
    switch ($size) {
        case 'xs':
            return css`
                width: 16px;
                height: 16px;
            `;
        case 'sm':
            return css`
                width: 24px;
                height: 24px;
            `;
        case 'lg':
        default:
            return css`
                width: ${DisplayTypeSize.Color.width};
                height: ${DisplayTypeSize.Color.height};
        `;
    }
}

const colorSwatchCommonStyling = css<ColorStyleProps>`
    position: relative;
    display: block;

    ${baseOutline}
    ${({ $size }) => swatchStateStyling($size)}
    ${({ $colors }) => $colors && getColorStyling($colors)}
    ${({ $size }) => getDimensions($size)}
    ${({ $isSelected, $size }) => $isSelected && $size && selectedStyling($size)}

    ${({ $isSelected, $size }) =>
        $isSelected &&
        $size &&
        css`
            box-shadow: inset 0 0px 0 ${$size === 'sm' ? '3px' : '4px'} ${({ theme }) => theme.ColorBorderFocusRingInverted};
        `}

    &:is(:focus-visible, :active, :hover, :hover:focus) {
        box-shadow: inset 0 0px 0 ${({ $size }) => ($size === 'sm' ? '3px' : '4px')} ${({ theme }) => theme.ColorBorderFocusRingInverted};
    }
`;

const LinkColorSwatch = styled(LinkAction)<ColorStyleProps>`
    ${colorSwatchCommonStyling}
`;

const StaticColorSwatch = styled.div<ColorStyleProps>`
    ${colorSwatchCommonStyling}
`;

export const ProductImageContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    &:after {
        content: '';
        ${imageOverlayStyle}
    }
`;

const FallbackImage = styled(Image.WithScaling)`
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: ${({ theme }) => theme.Spacing4};
`;

export default function ColorSwatch({
    colors,
    isSelected,
    unavailable,
    size = 'lg',
    name,
    linkAction,
    onClick,
    fallbackImage,
    ...restProps
}: ColorSwatchProps & HTMLAttributes<HTMLSpanElement>) {
    const noColors = colors.length === 0;

    const content = (
        <>
            {noColors && fallbackImage && (
                <ProductImageContainer>
                    <FallbackImage
                        src={fallbackImage}
                        alt=""
                        sizes={Number.parseInt(DisplayTypeSize.Color.width)}
                        transformType="SquareScale"
                        imageType="product"
                    />
                </ProductImageContainer>
            )}
            {unavailable && <DiagonalLines size={size} />}
        </>
    );

    if (onClick) {
        return (
            <StaticColorSwatch
                aria-label={name}
                title={name}
                $colors={colors}
                $isSelected={isSelected}
                $size={size}
                onClick={onClick}
                as="button"
                {...restProps}
            >
                {content}
            </StaticColorSwatch>
        );
    }

    if (!linkAction) {
        return (
            <StaticColorSwatch
                aria-label={name}
                title={name}
                $colors={colors}
                $isSelected={isSelected}
                $size={size}
                {...restProps}
            >
                {content}
            </StaticColorSwatch>
        );
    }

    return (
        <LinkColorSwatch
            action={linkAction}
            aria-label={name}
            title={name}
            $colors={colors}
            $isSelected={isSelected}
            $size={size}
            {...restProps}
        >
            {content}
        </LinkColorSwatch>
    );
}
